if (!customElements.get('curtis-index-preview')) {
  customElements.define(
    'curtis-index-preview',
    class CurtisIndexPreview extends HTMLElement {
      connectedCallback() {
        this.links = [...this.querySelectorAll('[data-preview-link]')];
        this.images = [...this.querySelectorAll('[data-preview-image]')];
        this.captionName = this.querySelector('[data-preview-caption-name]');
        this.captionMeta = this.querySelector('[data-preview-caption-meta]');
        this.captionLink = this.querySelector('[data-preview-caption-link]');
        if (!this.links.length) return;

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.currentIndex = -1;
        this.stopped = false;
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.shownImage = null;
        this.activate(0, { animate: false });

        // Hover or keyboard focus takes over for good: the loop stops until the page reloads,
        // and the last item the visitor chose stays shown
        for (const [index, link] of this.links.entries()) {
          link.addEventListener('mouseenter', () => this.choose(index), { signal });
          link.addEventListener('focus', () => this.choose(index), { signal });
        }

        this.plate = this.querySelector('.index-preview__plate');

        // The photo itself opens the active item when there is a caption link to follow
        if (this.captionLink) {
          this.plate.classList.add('is-linked');
          this.plate.addEventListener('click', (event) => {
            if (!event.target.closest('a')) window.location.href = this.captionLink.href;
          }, { signal });
        }

        // Phones, scrub mode: the name that has just passed under the pinned photo is the active one.
        // Scrolling drives it, so the timed loop stays off while that applies.
        this.phone = window.matchMedia('(max-width: 749px)');
        this.scrub = Boolean(this.closest('.index-preview--scrub'));
        if (this.scrub) {
          let frame = 0;
          const follow = () => {
            frame = 0;
            if (!this.phone.matches) return;
            // The first name fully clear of the photo is the one being read
            const line = this.plate.getBoundingClientRect().bottom - 4;
            let index = this.links.findIndex((link) => link.getBoundingClientRect().top >= line);
            if (index < 0) index = this.links.length - 1;
            this.activate(index);
          };
          window.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(follow); }, { passive: true, signal });
          this.phone.addEventListener('change', () => { if (this.phone.matches) this.stopLoop(); follow(); }, { signal });
          follow();
        }

        // Auto-advance slowly through the items, only while the list is on screen
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          if (this.visible) this.startLoop();
          else this.stopLoop();
        }, { threshold: 0.25 });
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.observer?.disconnect();
        this.stopLoop();
        clearTimeout(this.photoTimer);
      }

      startLoop() {
        if (this.stopped || this.loopTimer || this.links.length < 2) return;
        if (this.scrub && this.phone.matches) return;
        this.loopTimer = setInterval(() => {
          this.activate((this.currentIndex + 1) % this.links.length);
        }, CurtisIndexPreview.LOOP_INTERVAL);
      }

      stopLoop() {
        clearInterval(this.loopTimer);
        this.loopTimer = null;
      }

      choose(index) {
        this.stopped = true;
        this.stopLoop();
        this.activate(index, { delay: CurtisIndexPreview.HOVER_DELAY });
      }

      // The list highlight responds instantly; the photo waits for the target to settle
      // so fast scrolling or sweeping across names doesn't queue a burst of fades.
      activate(index, { animate = true, delay = CurtisIndexPreview.SCROLL_DELAY } = {}) {
        if (index < 0 || index === this.currentIndex) return;
        this.currentIndex = index;

        for (const [i, item] of this.links.entries()) {
          item.classList.toggle('is-active', i === index);
        }

        clearTimeout(this.photoTimer);
        if (!animate || this.reducedMotion) {
          this.showPhoto(index, false);
        } else {
          this.photoTimer = setTimeout(() => this.showPhoto(index, true), delay);
        }
      }

      showPhoto(index, animate) {
        const link = this.links[index];
        const next = this.images.find((image) => image.dataset.previewImage === link.dataset.previewLink);

        if (next && next !== this.shownImage) {
          for (const image of this.images) image.classList.toggle('is-current', image === next);

          // Layered crossfade: the incoming photo fades and settles on top of whatever is
          // visible now; anything beneath is hidden only once it is fully covered.
          this.layer = (this.layer || 1) + 1;
          next.style.zIndex = this.layer;
          next.style.visibility = 'visible';
          next.getAnimations().forEach((animation) => animation.cancel());

          // Videos only play while their layer is on screen
          if (!this.reducedMotion) next.querySelector('video')?.play().catch(() => {});

          const hideBeneath = () => {
            for (const image of this.images) {
              if (Number(image.style.zIndex || 0) < Number(next.style.zIndex)) {
                image.style.visibility = 'hidden';
                image.querySelector('video')?.pause();
              }
            }
          };

          if (animate) {
            next
              .animate(
                [
                  { opacity: 0, transform: 'scale(1.04)' },
                  { opacity: 1, transform: 'scale(1)' },
                ],
                { duration: CurtisIndexPreview.FADE_DURATION, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
              )
              .finished.then(hideBeneath)
              .catch(() => {});
          } else {
            hideBeneath();
          }
          this.shownImage = next;
        }

        if (this.captionName) this.captionName.textContent = link.dataset.previewName;
        if (this.captionMeta) this.captionMeta.textContent = link.dataset.previewMeta;
        if (this.captionLink) this.captionLink.href = link.href;
      }

      static SCROLL_DELAY = 180;
      static LOOP_INTERVAL = 4000;
      static HOVER_DELAY = 90;
      static FADE_DURATION = 700;
    },
  );
}
